import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueuedOfflineError, api, type ApiError, type ListEventCapturesResult } from '@whinerp/client';
import { parseBadge } from '@whinerp/contracts/badge';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Capturing people at the stand (G-163).
 *
 * A handheld scanner types a badge's code into the scan field; it is read into
 * the form for somebody to check. A capture that cannot reach the server - a
 * hall with no signal - is kept by the product's offline queue and sent later;
 * its reference was made here, so sending it twice is still one capture.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const EMPTY = { firstName: '', lastName: '', email: '', phone: '', company: '', title: '', note: '', interest: '' };

type Pending = Parameters<typeof api.captureAtEvent>[0]['body'];

const newRef = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

export function EventCapturePanel({ eventId }: { eventId: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [method, setMethod] = useState<'badge' | 'form'>('form');
  const [scan, setScan] = useState('');
  const [said, setSaid] = useState<string | null>(null);

  const captures = useQuery<ListEventCapturesResult, ApiError>({
    queryKey: ['event-captures', eventId],
    queryFn: () => api.listEventCaptures({ query: { eventId } }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['event-captures', eventId] });

  const save = useMutation({
    mutationFn: (body: Pending) => api.captureAtEvent({ body }),
    onSuccess: (result) => {
      setForm(EMPTY);
      setScan('');
      setMethod('form');
      setSaid(result.duplicate ? t('capture.duplicate') : result.created ? t('capture.newLead') : t('capture.known'));
      refresh();
    },
    onError: (error) => {
      // No signal: the product's offline queue kept it, to send when there is one.
      if (!(error instanceof QueuedOfflineError)) return;
      setForm(EMPTY);
      setScan('');
      setSaid(t('capture.queued'));
    },
  });

  const readScan = (text: string) => {
    const fields = parseBadge(text);
    setMethod('badge');
    setForm({
      ...form,
      firstName: fields.firstName ?? '',
      lastName: fields.lastName ?? '',
      email: fields.email ?? '',
      phone: fields.phone ?? '',
      company: fields.company ?? '',
      title: fields.title ?? '',
    });
  };

  const field = (key: keyof typeof EMPTY, label: string, type = 'text') => (
    <label className="flex flex-col gap-1 text-meta text-ink-meta">
      {label}
      <input className={INPUT} type={type} data-capture-field={key} value={form[key]}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
    </label>
  );

  return (
    <Panel data-region="event-capture">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('capture.title')}</SectionLabel>
        <label className="flex flex-col gap-1 text-meta text-ink-meta">
          {t('capture.scan')}
          <textarea className="min-h-16 rounded-control border border-control bg-surface p-2 text-body text-ink" data-capture-scan
            value={scan} placeholder={t('capture.scanHint')}
            onChange={(event) => setScan(event.target.value)}
            onBlur={() => scan.trim() && readScan(scan)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && scan.trim()) {
                event.preventDefault();
                readScan(scan);
              }
            }} />
        </label>
        <form data-capture-form className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate({
              eventId,
              clientRef: newRef(),
              method,
              firstName: form.firstName || null,
              lastName: form.lastName || null,
              email: form.email || null,
              phone: form.phone || null,
              company: form.company || null,
              title: form.title || null,
              interest: (form.interest || null) as Pending['interest'],
              note: form.note || null,
              capturedAt: new Date().toISOString(),
            });
          }}>
          {field('firstName', t('capture.firstName'))}
          {field('lastName', t('capture.lastName'))}
          {field('email', t('capture.email'), 'email')}
          {field('phone', t('capture.phone'), 'tel')}
          {field('company', t('capture.company'))}
          {field('title', t('capture.jobTitle'))}
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('capture.interest')}
            <select className={INPUT} data-capture-field="interest" value={form.interest}
              onChange={(event) => setForm({ ...form, interest: event.target.value })}>
              <option value="">—</option>
              {(['hot', 'warm', 'cold'] as const).map((level) => (
                <option key={level} value={level}>{t(`capture.interest.${level}`)}</option>
              ))}
            </select>
          </label>
          {field('note', t('capture.note'))}
          <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
            <Button intent="primary" size="small" type="submit" data-capture-save disabled={save.isPending}>{t('capture.save')}</Button>
            {said ? <span className="text-dense text-ink" data-capture-said>{said}</span> : null}
          </div>
          {save.error && !(save.error instanceof QueuedOfflineError) ? (
            <p className="sm:col-span-2 text-dense text-danger-text" data-capture-error>{errorText(save.error as ApiError)}</p>
          ) : null}
        </form>
        {captures.data && captures.data.captures.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-capture-list>
            {captures.data.captures.slice(0, 50).map((entry) => (
              <li key={entry.captureId} data-capture={entry.captureId} className="flex flex-wrap gap-2">
                <span className="text-ink">{entry.name}</span>
                {entry.company ? <span className="text-ink-600">{entry.company}</span> : null}
                {entry.interest ? <span className="text-ink-meta">{t(`capture.interest.${entry.interest as 'hot'}`)}</span> : null}
                <span className="ms-auto text-ink-meta">{formatDateTime(entry.capturedAt, format)}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
